import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Building2, Users, KeyRound, ScrollText, Activity,
  LogOut, RefreshCw, Ban, CheckCircle2, ArrowLeft, Images,
  Plus, Settings2, Copy, Sparkles, ShieldAlert, Trash2, Atom, Zap, Gauge, Cpu, Network, Mail, Boxes,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EnterpriseInferenceConsole } from "@/components/EnterpriseInferenceConsole";
import { ModuleControlPlane } from "@/components/ModuleControlPlane";
import { MigrationCockpit } from "@/components/MigrationCockpit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <Card className="p-4 flex items-center gap-3" data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[var(--qn-primary-muted)] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[var(--qn-primary)]" />
    </div>
    <div>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">{label}</div>
    </div>
  </Card>
);

const Sparkline = ({ values = [], color = "var(--qn-primary)", w = 72, h = 18 }) => {
  if (!values.length) return <span className="text-[10px] text-[var(--qn-foreground-subtle)]">—</span>;
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * w;
    const y = h - (v / max) * (h - 3) - 1.5;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

export default function PlatformAdmin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [keys, setKeys] = useState([]);
  const [audit, setAudit] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryStatus, setGalleryStatus] = useState(null);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", admin_email: "", admin_password: "", plan: "enterprise" });
  const [manageTenant, setManageTenant] = useState(null);
  const [manageFeatures, setManageFeatures] = useState({});
  const [generatedKey, setGeneratedKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [security, setSecurity] = useState(null);
  const [tenantKeys, setTenantKeys] = useState([]);
  const [quantum, setQuantum] = useState(null);
  const [leapUsage, setLeapUsage] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [cqm, setCqm] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [ciBaseline, setCiBaseline] = useState(null);
  const [rawQpu, setRawQpu] = useState(null);
  const [batchRouting, setBatchRouting] = useState(null);
  const [scaleOut, setScaleOut] = useState(null);
  const [dqm, setDqm] = useState(null);
  const [leapEmailOpen, setLeapEmailOpen] = useState(false);
  const [qBusy, setQBusy] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, u, k, a, h, f, sec, q] = await Promise.all([
        axios.get(`${API}/platform/stats`),
        axios.get(`${API}/platform/tenants`),
        axios.get(`${API}/platform/users`),
        axios.get(`${API}/platform/api-keys`),
        axios.get(`${API}/platform/audit?limit=100`),
        axios.get(`${API}/platform/system-health`),
        axios.get(`${API}/platform/features`),
        axios.get(`${API}/platform/security?limit=50`),
        axios.get(`${API}/platform/quantum/metrics?limit=25`),
      ]);
      setStats(s.data); setTenants(t.data.tenants); setUsers(u.data.users);
      setKeys(k.data.api_keys); setAudit(a.data.entries); setHealth(h.data);
      setFeatureCatalog(f.data.features); setSecurity(sec.data); setQuantum(q.data);
      axios.get(`${API}/platform/quantum/leap-usage`).then(r => setLeapUsage(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/benchmark`).then(r => setBenchmark(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/benchmarks?limit=15`).then(r => setBenchmarks(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/ci-baseline`).then(r => setCiBaseline(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/raw-qpu`).then(r => setRawQpu(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/batch-routing`).then(r => setBatchRouting(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/scale-out`).then(r => setScaleOut(r.data)).catch(() => {});
      axios.get(`${API}/platform/quantum/haven-dqm`).then(r => setDqm(r.data)).catch(() => {});
    } catch (e) {
      toast.error("Failed to load platform data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const probeLeap = async () => {
    setQBusy("leap");
    try {
      const r = await axios.get(`${API}/platform/quantum/leap-usage?probe=true`);
      setLeapUsage(r.data);
      const av = r.data?.availability;
      toast[av?.available ? "success" : "warning"](av?.available ? "Leap QPU is available" : (av?.message || "Leap unavailable"));
    } catch { toast.error("Probe failed"); }
    finally { setQBusy(""); }
  };

  const runBenchmark = async () => {
    setQBusy("bench");
    try {
      const r = await axios.post(`${API}/platform/quantum/benchmark/run`, { prefer_quantum: true });
      setBenchmark(r.data);
      toast.success(`Benchmark complete — ${r.data.problem_count} problems`);
    } catch { toast.error("Benchmark failed"); }
    finally { setQBusy(""); }
  };

  const loadCqm = async () => {
    setQBusy("cqm");
    try {
      const r = await axios.get(`${API}/platform/quantum/cqm-prototype?k=6&per_state_cap=3`);
      setCqm(r.data);
      toast.success(`CQM solved via ${r.data.solver_used}`);
    } catch { toast.error("CQM prototype failed"); }
    finally { setQBusy(""); }
  };

  const runBenchmarks = async () => {
    setQBusy("benchmarks");
    try {
      await axios.post(`${API}/platform/quantum/benchmarks/run`, { prefer_quantum: true, time_limit: 20, clear_previous: true });
      toast.info("Head-to-head running in background (full-scale QUBO→MIP is heavy)…");
      // Poll status until the background job finishes (~1-3 min).
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const st = await axios.get(`${API}/platform/quantum/benchmarks/status`);
        if (!st.data.running) {
          if (st.data.last_error) toast.error(`Benchmark error: ${st.data.last_error}`);
          else toast.success(`HiGHS head-to-head complete — ${st.data.ran} problems`);
          break;
        }
      }
      const agg = await axios.get(`${API}/platform/quantum/benchmarks?limit=15`);
      setBenchmarks(agg.data);
      axios.get(`${API}/platform/quantum/ci-baseline`).then(r => setCiBaseline(r.data)).catch(() => {});
    } catch { toast.error("Benchmark run failed"); }
    finally { setQBusy(""); }
  };

  const runRawQpu = async () => {
    setQBusy("rawqpu");
    try {
      await axios.post(`${API}/platform/quantum/raw-qpu/run`, { prefer_quantum: true, k: 24, num_reads: 1000 });
      toast.info("Direct QPU anneal running in background (auto-embedding + live solve)…");
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API}/platform/quantum/raw-qpu/status`);
        if (!st.data.running) {
          if (st.data.last_error) toast.error(`Raw-QPU error: ${st.data.last_error}`);
          else toast[st.data.raw_qpu_ran ? "success" : "warning"](st.data.raw_qpu_ran
            ? "Raw QPU anneal recorded"
            : "QPU unavailable — classical annealing + exact optimum shown as baseline");
          break;
        }
      }
      const r = await axios.get(`${API}/platform/quantum/raw-qpu`);
      setRawQpu(r.data);
    } catch { toast.error("Raw-QPU run failed"); }
    finally { setQBusy(""); }
  };

  const runBatchRouting = async () => {
    setQBusy("batch");
    try {
      await axios.post(`${API}/platform/quantum/batch-routing/run`, {});
      toast.info("Batch Routing QUBO scenarios running (small/medium solved, enterprise+ sized)…");
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const st = await axios.get(`${API}/platform/quantum/batch-routing/status`);
        if (!st.data.running) {
          if (st.data.last_error) toast.error(`Batch benchmark error: ${st.data.last_error}`);
          else toast.success(`Batch Routing QUBO complete — ${st.data.scenarios} scenarios`);
          break;
        }
      }
      const r = await axios.get(`${API}/platform/quantum/batch-routing`);
      setBatchRouting(r.data);
    } catch { toast.error("Batch routing run failed"); }
    finally { setQBusy(""); }
  };

  const runDqm = async () => {
    setQBusy("dqm");
    try {
      await axios.post(`${API}/platform/quantum/haven-dqm/run`, { prefer_quantum: true });
      toast.info("HAVEN DQM building (config one-hot → discrete) + solving…");
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API}/platform/quantum/haven-dqm/status`);
        if (!st.data.running) {
          if (st.data.last_error) toast.error(`DQM error: ${st.data.last_error}`);
          else toast.success(`HAVEN DQM solved via ${st.data.solver}`);
          break;
        }
      }
      const r = await axios.get(`${API}/platform/quantum/haven-dqm`);
      setDqm(r.data);
    } catch { toast.error("DQM run failed"); }
    finally { setQBusy(""); }
  };

  const logRestoration = async () => {
    try { await axios.post(`${API}/platform/quantum/leap-restoration-request`, {}); } catch { /* non-blocking */ }
    setLeapEmailOpen(true);
  };

  const runScaleOut = async () => {
    setQBusy("scaleout");
    try {
      await axios.post(`${API}/platform/quantum/scale-out/run`, {});
      toast.info("Scale-out studies running (HAVEN Tier-4 ~12k vars, CSH Large ~10k vars)…");
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await axios.get(`${API}/platform/quantum/scale-out/status`);
        if (!st.data.running) {
          if (st.data.last_error) toast.error(`Scale-out error: ${st.data.last_error}`);
          else toast.success("Scale-out studies complete");
          break;
        }
      }
      const r = await axios.get(`${API}/platform/quantum/scale-out`);
      setScaleOut(r.data);
    } catch { toast.error("Scale-out run failed"); }
    finally { setQBusy(""); }
  };

  const LEAP_EMAIL = {
    to: "support@dwavesys.com",
    subject: "Leap Solver-Access Time Restoration Request — QNNEAL D-Wave Technical Session",
    body: `Dear D-Wave Solutions Engineering Team,

We are preparing for an upcoming technical session with your team and require restoration of Leap solver-access time to complete our pre-session benchmark runs.

Our platform (QNNEAL by Global InTech AS) has the following active use cases ready for live Leap runs:

1. HAVEN institutional portfolio optimization (6,370 binary variables, 38,293 pairwise terms) — LeapHybridSampler BQM and LeapHybridCQMSampler CQM both wired and ready.
2. CSH grant pursuit portfolio (56 to 10,200 binary variables) — LeapHybridCQMSampler CQM wired and ready.
3. Proxy Batch Routing QUBO (N=200, M=100 — 20,206 variables, approximately 2 million coupling terms) — LeapHybridSampler BQM and LeapHybridCQMSampler CQM both wired and ready.

All solvers resolve correctly via live inventory. The token authenticates. The only blocker is exhausted solver-access time.

We would appreciate emergency time restoration ahead of the session. Please let us know what information you need from our side.

Best regards,
[Your name]
Global InTech AS — QNNEAL`,
  };

  const toggleTenant = async (org_id, suspended) => {
    try {
      await axios.put(`${API}/platform/tenants/${org_id}/status`, { suspended: !suspended });
      toast.success(!suspended ? "Tenant suspended" : "Tenant reactivated");
      loadAll();
    } catch { toast.error("Action failed"); }
  };

  const changeRole = async (user_id, role) => {
    try {
      await axios.put(`${API}/platform/users/${user_id}`, { role });
      toast.success("Role updated");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const toggleUser = async (user_id, disabled) => {
    try {
      await axios.put(`${API}/platform/users/${user_id}`, { disabled: !disabled });
      toast.success(!disabled ? "User disabled" : "User enabled");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const revokeKey = async (key_preview) => {
    try {
      await axios.post(`${API}/platform/api-keys/revoke`, { key_preview });
      toast.success("Key revoked");
      loadAll();
    } catch { toast.error("Revoke failed"); }
  };

  const pollGallery = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/platform/investor-screenshots/status`);
      setGalleryStatus(data);
      if (data.process_alive || data.state === "running") {
        setTimeout(pollGallery, 3000);
      } else {
        setGalleryBusy(false);
        if (data.state === "done") toast.success(`Gallery refreshed — ${data.done}/${data.total} screenshots`);
        else if (data.state === "error") toast.error("Gallery refresh failed");
      }
    } catch {
      setGalleryBusy(false);
    }
  }, []);

  const refreshGallery = async () => {
    setGalleryBusy(true);
    try {
      const { data } = await axios.post(`${API}/platform/investor-screenshots/refresh`);
      if (data.status === "already_running") toast.info("A refresh is already running");
      else toast.info("Screenshot refresh started");
      pollGallery();
    } catch (e) {
      setGalleryBusy(false);
      toast.error(e.response?.data?.detail || "Could not start refresh");
    }
  };

  const openManage = async (t) => {
    setManageTenant(t);
    setManageFeatures({ ...(t.features || {}) });
    setGeneratedKey(null);
    setTenantKeys([]);
    try {
      const { data } = await axios.get(`${API}/platform/tenants/${t.org_id}/api-keys`);
      setTenantKeys(data.api_keys || []);
    } catch { /* keys list is best-effort */ }
  };

  const revokeTenantKey = async (key_preview) => {
    try {
      await axios.post(`${API}/platform/api-keys/revoke`, { key_preview });
      toast.success("Key revoked");
      setTenantKeys((prev) => prev.map((k) => k.key_preview === key_preview ? { ...k, is_active: false } : k));
      loadAll();
    } catch { toast.error("Revoke failed"); }
  };

  const unblockIp = async (ip) => {
    try {
      await axios.post(`${API}/platform/security/unblock`, { ip });
      toast.success(`Unblocked ${ip}`);
      loadAll();
    } catch { toast.error("Unblock failed"); }
  };

  const exportSecurityLog = async () => {
    try {
      const res = await axios.get(`${API}/platform/security/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `qnneal_security_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Security log exported");
    } catch { toast.error("Export failed"); }
  };

  const saveEntitlements = async () => {
    if (!manageTenant) return;
    setBusy(true);
    try {
      await axios.put(`${API}/platform/tenants/${manageTenant.org_id}/entitlements`, { features: manageFeatures });
      toast.success("Entitlements updated");
      await loadAll();
      setManageTenant(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const generateKey = async () => {
    if (!manageTenant) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/platform/tenants/${manageTenant.org_id}/api-keys`, {
        name: `${manageTenant.name} Swarm + Proxy Key`,
        scopes: ["Swarm/execute", "LLM-Gateway/chat"],
      });
      setGeneratedKey(data.full_key);
      toast.success("API key generated — copy it now, it won't be shown again");
      try {
        const { data: kd } = await axios.get(`${API}/platform/tenants/${manageTenant.org_id}/api-keys`);
        setTenantKeys(kd.api_keys || []);
      } catch { /* ignore */ }
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Key generation failed"); }
    finally { setBusy(false); }
  };

  const createTenant = async () => {
    if (!createForm.name.trim() || !createForm.admin_email.trim()) {
      toast.error("Name and admin email are required"); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/platform/tenants`, {
        name: createForm.name.trim(),
        admin_email: createForm.admin_email.trim(),
        admin_password: createForm.admin_password || null,
        plan: createForm.plan,
      });
      if (data.admin_password) {
        toast.success(`Tenant created. Generated password: ${data.admin_password}`, { duration: 12000 });
      } else {
        toast.success("Tenant created");
      }
      setCreateOpen(false);
      setCreateForm({ name: "", admin_email: "", admin_password: "", plan: "enterprise" });
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--qn-background)] text-[var(--qn-foreground)]" data-testid="platform-admin-page">
      {/* Header */}
      <header className="border-b border-[var(--qn-border)] bg-[var(--qn-card)] px-8 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-[var(--qn-primary)]" />
            <h1 className="text-xl font-bold">Platform Admin</h1>
            <Badge className="bg-red-500/15 text-red-400 border-red-500/30">ROOT</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="px-8 py-6 max-w-[1400px] mx-auto space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Building2} label="Tenants (orgs)" value={stats?.total_tenants ?? "—"} testid="stat-tenants" />
          <StatCard icon={Ban} label="Suspended" value={stats?.suspended_tenants ?? "—"} testid="stat-suspended" />
          <StatCard icon={Users} label="Users" value={stats?.total_users ?? "—"} testid="stat-users" />
          <StatCard icon={KeyRound} label="Active API Keys" value={stats?.active_api_keys ?? "—"} testid="stat-keys" />
        </div>

        <Tabs defaultValue="tenants">
          <TabsList data-testid="platform-tabs">
            <TabsTrigger value="tenants" data-testid="tab-tenants"><Building2 className="w-4 h-4 mr-1" />Tenants</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1" />Users</TabsTrigger>
            <TabsTrigger value="keys" data-testid="tab-keys"><KeyRound className="w-4 h-4 mr-1" />API Keys</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-4 h-4 mr-1" />Audit</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><ShieldAlert className="w-4 h-4 mr-1" />Security</TabsTrigger>
            <TabsTrigger value="enterprise" data-testid="tab-enterprise"><ShieldAlert className="w-4 h-4 mr-1" />Enterprise</TabsTrigger>
            <TabsTrigger value="modules" data-testid="tab-modules"><Boxes className="w-4 h-4 mr-1" />Modules</TabsTrigger>
            <TabsTrigger value="cockpit" data-testid="tab-cockpit"><Network className="w-4 h-4 mr-1" />Migration</TabsTrigger>
            <TabsTrigger value="quantum" data-testid="tab-quantum"><Atom className="w-4 h-4 mr-1" />Quantum</TabsTrigger>
            <TabsTrigger value="health" data-testid="tab-health"><Activity className="w-4 h-4 mr-1" />System Health</TabsTrigger>
          </TabsList>

          {/* Enterprise Inference */}
          <TabsContent value="enterprise">
            <EnterpriseInferenceConsole />
          </TabsContent>

          {/* Module Control Plane */}
          <TabsContent value="modules">
            <ModuleControlPlane />
          </TabsContent>

          {/* Migration Cockpit (Slice 6G-D) */}
          <TabsContent value="cockpit">
            <MigrationCockpit />
          </TabsContent>

          {/* Tenants */}
          <TabsContent value="tenants">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm text-[var(--qn-foreground-muted)]">Manage organizations, feature entitlements, and API keys</h3>
                <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="create-tenant-btn">
                  <Plus className="w-4 h-4 mr-1" /> Create Tenant
                </Button>
              </div>
              <Table data-testid="tenants-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead><TableHead>Owner</TableHead>
                    <TableHead>Plan</TableHead><TableHead>Members</TableHead>
                    <TableHead>API Keys</TableHead><TableHead>Runs</TableHead>
                    <TableHead>Spend</TableHead><TableHead>Trend (7d)</TableHead><TableHead>Entitlements</TableHead>
                    <TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => {
                    const onCount = Object.values(t.features || {}).filter(Boolean).length;
                    const total = Object.keys(t.features || {}).length;
                    return (
                    <TableRow key={t.org_id} data-testid={`tenant-row-${t.org_id}`}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="text-[var(--qn-foreground-muted)]">{t.owner_email}</TableCell>
                      <TableCell><Badge variant="outline">{t.plan}</Badge></TableCell>
                      <TableCell>{t.member_count}</TableCell>
                      <TableCell>{t.active_api_keys}</TableCell>
                      <TableCell data-testid={`tenant-runs-${t.org_id}`}>{t.swarm_runs ?? 0}</TableCell>
                      <TableCell data-testid={`tenant-spend-${t.org_id}`}>${(t.spend ?? 0).toFixed(2)}</TableCell>
                      <TableCell data-testid={`tenant-trend-${t.org_id}`}>
                        <div className="flex flex-col gap-0.5" title="7-day runs (blue) and spend (green)">
                          <div className="flex items-center gap-1"><span className="text-[9px] text-[var(--qn-foreground-subtle)] w-3">R</span><Sparkline values={t.trend?.runs || []} color="var(--qn-primary)" /></div>
                          <div className="flex items-center gap-1"><span className="text-[9px] text-[var(--qn-foreground-subtle)] w-3">$</span><Sparkline values={t.trend?.spend || []} color="#10b981" /></div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]" data-testid={`tenant-entitlements-${t.org_id}`}>{onCount}/{total} on</Badge>
                      </TableCell>
                      <TableCell>
                        {t.suspended
                          ? <Badge className="bg-red-500/15 text-red-400 border-red-500/30">Suspended</Badge>
                          : <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => openManage(t)}
                            data-testid={`tenant-manage-${t.org_id}`}>
                            <Settings2 className="w-4 h-4 mr-1" />Manage
                          </Button>
                          <Button size="sm" variant={t.suspended ? "outline" : "destructive"}
                            onClick={() => toggleTenant(t.org_id, t.suspended)}
                            data-testid={`tenant-toggle-${t.org_id}`}>
                            {t.suspended ? <><CheckCircle2 className="w-4 h-4 mr-1" />Reactivate</> : <><Ban className="w-4 h-4 mr-1" />Suspend</>}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <Card className="p-4">
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Email</TableHead>
                    <TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-medium">{u.name}{u.is_platform_admin && <Badge className="ml-2 bg-red-500/15 text-red-400 border-red-500/30">ROOT</Badge>}</TableCell>
                      <TableCell className="text-[var(--qn-foreground-muted)]">{u.email}</TableCell>
                      <TableCell>
                        {u.is_platform_admin ? (
                          <Badge variant="outline">platform_admin</Badge>
                        ) : (
                          <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                            <SelectTrigger className="w-32 h-8" data-testid={`user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">owner</SelectItem>
                              <SelectItem value="admin">admin</SelectItem>
                              <SelectItem value="developer">developer</SelectItem>
                              <SelectItem value="viewer">viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.disabled
                          ? <Badge className="bg-red-500/15 text-red-400 border-red-500/30">Disabled</Badge>
                          : <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge>}
                      </TableCell>
                      <TableCell>
                        {!u.is_platform_admin && (
                          <Button size="sm" variant={u.disabled ? "outline" : "destructive"}
                            onClick={() => toggleUser(u.user_id, u.disabled)}
                            data-testid={`user-toggle-${u.user_id}`}>
                            {u.disabled ? "Enable" : "Disable"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* API Keys */}
          <TabsContent value="keys">
            <Card className="p-4">
              <Table data-testid="keys-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Preview</TableHead><TableHead>Owner</TableHead>
                    <TableHead>Scopes</TableHead><TableHead>Project</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keys.map((k) => (
                    <TableRow key={k.key_preview} data-testid={`key-row-${k.key_preview}`}>
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="font-mono text-xs">{k.key_preview}</TableCell>
                      <TableCell className="text-[var(--qn-foreground-muted)]">{k.owner_email || k.user_id}</TableCell>
                      <TableCell className="text-xs">{(k.scopes || []).join(", ")}</TableCell>
                      <TableCell>{k.project || <span className="text-[var(--qn-foreground-subtle)]">all</span>}</TableCell>
                      <TableCell>
                        {k.is_active
                          ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge>
                          : <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-500/30">Revoked</Badge>}
                      </TableCell>
                      <TableCell>
                        {k.is_active && (
                          <Button size="sm" variant="destructive" onClick={() => revokeKey(k.key_preview)}
                            data-testid={`key-revoke-${k.key_preview}`}>Revoke</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Audit */}
          <TabsContent value="audit">
            <Card className="p-4">
              <Table data-testid="audit-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead><TableHead>Source</TableHead>
                    <TableHead>Tenant</TableHead><TableHead>Action</TableHead><TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.map((e, i) => (
                    <TableRow key={i} data-testid={`audit-row-${i}`}>
                      <TableCell className="text-xs whitespace-nowrap">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{e._source_collection}</Badge></TableCell>
                      <TableCell>{e.tenant_id || "—"}</TableCell>
                      <TableCell className="font-medium">{e.action || e.event_type || "—"}</TableCell>
                      <TableCell className="text-xs text-[var(--qn-foreground-muted)] max-w-[360px] truncate">{e.detail ? JSON.stringify(e.detail) : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Security */}
          <TabsContent value="security">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm text-[var(--qn-foreground-muted)]">Auth failures, blocked IPs, scope violations and entitlement denials</h3>
              <Button size="sm" variant="outline" onClick={exportSecurityLog} data-testid="export-security-btn">
                <ScrollText className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard icon={ShieldAlert} label="Auth Failures" value={security?.summary?.auth_failures ?? "—"} testid="sec-stat-auth" />
              <StatCard icon={KeyRound} label="Scope Violations" value={security?.summary?.scope_violations ?? "—"} testid="sec-stat-scope" />
              <StatCard icon={Ban} label="Entitlement Denials" value={security?.summary?.entitlement_denials ?? "—"} testid="sec-stat-entitlement" />
              <StatCard icon={Activity} label="Blocked IPs" value={security?.summary?.blocked_ips ?? "—"} testid="sec-stat-blocked" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-4" data-testid="sec-blocked-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Ban className="w-4 h-4" /> Blocked IPs</h3>
                {(security?.blocked_ips || []).length === 0 ? (
                  <p className="text-sm text-[var(--qn-foreground-muted)]">No IPs are currently blocked.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>IP</TableHead><TableHead>Unblocks in</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {security.blocked_ips.map((b, i) => (
                        <TableRow key={i} data-testid={`blocked-ip-${i}`}>
                          <TableCell className="font-mono text-xs">{b.ip}</TableCell>
                          <TableCell className="text-xs">{b.seconds_remaining}s</TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => unblockIp(b.ip)} data-testid={`unblock-ip-${b.ip}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1" />Unblock
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              <Card className="p-4" data-testid="sec-entitlement-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Recent Entitlement Denials</h3>
                {(security?.entitlement_denials || []).length === 0 ? (
                  <p className="text-sm text-[var(--qn-foreground-muted)]">No entitlement denials recorded.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Tenant</TableHead><TableHead>Feature</TableHead><TableHead>Endpoint</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {security.entitlement_denials.slice(0, 25).map((e, i) => (
                        <TableRow key={i} data-testid={`entitlement-denial-${i}`}>
                          <TableCell className="text-xs whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                          <TableCell className="text-xs">{e.org_id}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{e.feature}</Badge></TableCell>
                          <TableCell className="text-xs font-mono truncate max-w-[160px]">{e.endpoint}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              <Card className="p-4" data-testid="sec-auth-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Recent Auth Failures</h3>
                {(security?.auth_failures || []).length === 0 ? (
                  <p className="text-sm text-[var(--qn-foreground-muted)]">No auth failures recorded.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Key</TableHead><TableHead>IP</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {security.auth_failures.slice(0, 25).map((e, i) => (
                        <TableRow key={i} data-testid={`auth-failure-${i}`}>
                          <TableCell className="text-xs whitespace-nowrap">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</TableCell>
                          <TableCell className="text-xs font-mono">{e.key_prefix || "—"}</TableCell>
                          <TableCell className="text-xs">{e.ip || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              <Card className="p-4" data-testid="sec-scope-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Recent Scope Violations</h3>
                {(security?.scope_violations || []).length === 0 ? (
                  <p className="text-sm text-[var(--qn-foreground-muted)]">No scope violations recorded.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Required</TableHead><TableHead>Path</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {security.scope_violations.slice(0, 25).map((e, i) => (
                        <TableRow key={i} data-testid={`scope-violation-${i}`}>
                          <TableCell className="text-xs whitespace-nowrap">{(e.created_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{e.required_scope}</Badge></TableCell>
                          <TableCell className="text-xs font-mono truncate max-w-[160px]">{e.path}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* System Health */}
          <TabsContent value="health">
            <Card className="p-6 space-y-4" data-testid="health-panel">
              <div>
                <h3 className="font-semibold mb-3">Data Source Connectivity</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {health && Object.entries(health.data_sources).map(([k, ok]) => {
                    const LABELS = { sam_gov: "SAM.gov", together_ai: "Together AI", fred: "FRED", dwave_token: "D-Wave", bls_qcew: "BLS QCEW", census: "Census", census_bps_permits: "Census BPS Permits", zillow_zori: "Zillow ZORI" };
                    return (
                    <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[var(--qn-surface-hover)]" data-testid={`health-${k}`}>
                      <span className="text-sm">{LABELS[k] || k.replace(/_/g, " ")}</span>
                      {ok
                        ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Connected</Badge>
                        : <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30">Missing Key</Badge>}
                    </div>
                    );
                  })}
                </div>
              </div>
              <div className="text-sm text-[var(--qn-foreground-muted)]">
                Linneman scans recorded: <b>{health?.linneman_scans ?? 0}</b>
                {health?.linneman_scan_states && Object.keys(health.linneman_scan_states).length > 0 && (
                  <span className="ml-2">
                    {Object.entries(health.linneman_scan_states).map(([st, n]) => (
                      <Badge key={st} variant="outline" className="ml-1 text-[10px]">{st}: {n}</Badge>
                    ))}
                  </span>
                )}
                <span className="ml-2">· Checked {health?.checked_at?.slice(0, 19).replace("T", " ")}</span>
              </div>
              {health?.recent_scans?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 mt-2">Recent Scans</h3>
                  <Table data-testid="recent-scans-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scan</TableHead><TableHead>Tenant</TableHead>
                        <TableHead>State</TableHead><TableHead>Phase</TableHead>
                        <TableHead>Progress</TableHead><TableHead>Compute</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {health.recent_scans.map((s) => (
                        <TableRow key={s.scan_id} data-testid={`scan-row-${s.scan_id}`}>
                          <TableCell className="font-mono text-xs">{s.scan_id}</TableCell>
                          <TableCell>{s.tenant_id}</TableCell>
                          <TableCell>
                            <Badge className={s.state === "completed"
                              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                              : s.state === "failed"
                                ? "bg-red-500/15 text-red-400 border-red-500/30"
                                : "bg-amber-500/15 text-amber-400 border-amber-500/30"}>{s.state}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{s.phase}</TableCell>
                          <TableCell className="text-xs">{s.progress_pct ?? 0}%</TableCell>
                          <TableCell className="text-xs">{s.compute_method || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>

            <Card className="p-6 space-y-3 mt-4" data-testid="investor-gallery-panel">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Images className="w-4 h-4" /> Investor Portal Screenshot Gallery
                  </h3>
                  <p className="text-sm text-[var(--qn-foreground-muted)] mt-1">
                    Re-capture all 20 screenshots (10 swarm/sim/PE pages × dark + light, incl. a live
                    sim run) and re-upload them so the Global InTech portal never shows a stale image.
                  </p>
                </div>
                <Button onClick={refreshGallery} disabled={galleryBusy}
                  data-testid="refresh-gallery-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${galleryBusy ? "animate-spin" : ""}`} />
                  {galleryBusy ? "Refreshing…" : "Refresh Gallery"}
                </Button>
              </div>
              {galleryStatus && galleryStatus.state !== "idle" && (
                <div className="text-sm" data-testid="gallery-status">
                  <div className="flex items-center gap-2">
                    <Badge className={galleryStatus.state === "done"
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                      : galleryStatus.state === "error"
                        ? "bg-red-500/15 text-red-400 border-red-500/30"
                        : "bg-amber-500/15 text-amber-400 border-amber-500/30"}>
                      {galleryStatus.state}
                    </Badge>
                    <span className="text-[var(--qn-foreground-muted)]">
                      {galleryStatus.done ?? 0}/{galleryStatus.total ?? 20} captured
                      {galleryStatus.current ? ` · ${galleryStatus.current}` : ""}
                    </span>
                  </div>
                  {galleryStatus.total > 0 && (
                    <div className="h-1.5 rounded-full bg-[var(--qn-surface-hover)] mt-2 overflow-hidden">
                      <div className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${Math.round(((galleryStatus.done ?? 0) / galleryStatus.total) * 100)}%` }} />
                    </div>
                  )}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="quantum">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm text-[var(--qn-foreground-muted)]">Live D-Wave solve telemetry — every row is genuinely measured from the QUBO solve paths (HAVEN, Grants, proxy rebalance)</h3>
              <Button size="sm" variant="outline" onClick={loadAll} data-testid="quantum-refresh-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Refresh
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard icon={Atom} label="Total Solves" value={quantum?.totals?.total_solves ?? "—"} testid="q-stat-total" />
              <StatCard icon={Zap} label="Hybrid (QPU) Solves" value={quantum?.totals?.hybrid_solves ?? "—"} testid="q-stat-hybrid" />
              <StatCard icon={Activity} label="Classical SA Solves" value={quantum?.totals?.sa_solves ?? "—"} testid="q-stat-sa" />
              <StatCard icon={Gauge} label="Cumulative QPU (s)" value={quantum?.totals?.cumulative_qpu_access_s ?? "—"} testid="q-stat-qpu" />
            </div>

            {/* Phase 2 — production formulation mix + Phase 3 — embedding quality */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <Card className="p-4" data-testid="q-formulation-panel">
                <h3 className="font-semibold mb-2 flex items-center gap-2"><Atom className="w-4 h-4" /> Production Formulation</h3>
                <p className="text-[11px] text-[var(--qn-foreground-muted)] mb-2">CQM is the production default for Tier-3+ HAVEN and CSH; BQM penalty encoding remains the fallback. DQM collapses HAVEN config one-hot groups to discrete variables.</p>
                <div className="flex flex-wrap gap-1.5">
                  {(quantum?.by_formulation || []).length === 0 ? (
                    <span className="text-sm text-[var(--qn-foreground-muted)]">No solves recorded yet.</span>
                  ) : (quantum.by_formulation.map((f, i) => (
                    <Badge key={i} data-testid={`q-formulation-${f.formulation}`}
                      variant={f.formulation === "bqm_penalty" ? "secondary" : "default"}
                      className={f.formulation !== "bqm_penalty" ? "bg-emerald-600 text-[10px]" : "text-[10px]"}>
                      {f.formulation === "cqm_native" ? "CQM Active" : f.formulation === "dqm_discrete" ? "DQM Active" : "BQM penalty"} · {f.count}
                    </Badge>
                  )))}
                </div>
              </Card>
              <Card className="p-4" data-testid="q-embedding-panel">
                <h3 className="font-semibold mb-2 flex items-center gap-2"><Network className="w-4 h-4" /> Embedding Quality (all hybrid solves)</h3>
                {quantum?.embedding_quality?.measured_rows ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">chain-break frac</span><span className="font-mono">{quantum.embedding_quality.avg_chain_break_fraction ?? "—"}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">physical qubits</span><span className="font-mono">{quantum.embedding_quality.avg_physical_qubits ?? "—"}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">max chain</span><span className="font-mono">{quantum.embedding_quality.max_chain_length ?? "—"}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">avg chain</span><span className="font-mono">{quantum.embedding_quality.avg_chain_length ?? "—"}</span></div>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--qn-foreground-muted)]" data-testid="q-embedding-pending">Pending live QPU runs. chain_break_fraction, physical_qubits, max/avg chain length are captured from <span className="font-mono">sampleset.info</span> for every hybrid solve — they populate the moment Leap access is restored (never fabricated).</p>
                )}
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <Card className="p-4" data-testid="q-solvetime-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Gauge className="w-4 h-4" /> Solve Time (ms)</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">p50</span><span className="font-mono">{quantum?.solve_time_ms?.p50 ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">p95</span><span className="font-mono">{quantum?.solve_time_ms?.p95 ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">p99</span><span className="font-mono">{quantum?.solve_time_ms?.p99 ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">max</span><span className="font-mono">{quantum?.solve_time_ms?.max ?? "—"}</span></div>
                </div>
              </Card>
              <Card className="p-4" data-testid="q-qputime-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Zap className="w-4 h-4" /> QPU Access Time (ms)</h3>
                {quantum?.qpu_access_time_ms?.count ? (
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">p50</span><span className="font-mono">{quantum.qpu_access_time_ms.p50}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">p95</span><span className="font-mono">{quantum.qpu_access_time_ms.p95}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">p99</span><span className="font-mono">{quantum.qpu_access_time_ms.p99}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">max</span><span className="font-mono">{quantum.qpu_access_time_ms.max}</span></div>
                  </div>
                ) : (
                  <p className="text-xs text-[var(--qn-foreground-muted)]">No QPU rows yet. Leap Hybrid is wired and the token authenticates, but live QPU solves populate here only when the Leap project has remaining solver-access time (currently falls back to classical SA).</p>
                )}
              </Card>
              <Card className="p-4" data-testid="q-size-panel">
                <h3 className="font-semibold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Problem Size (binary vars)</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">min</span><span className="font-mono">{quantum?.problem_size?.min_vars ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">avg</span><span className="font-mono">{quantum?.problem_size?.avg_vars ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">max</span><span className="font-mono">{quantum?.problem_size?.max_vars ?? "—"}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--qn-foreground-muted)]">success</span><span className="font-mono">{quantum?.totals?.success_rate_pct ?? "—"}%</span></div>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="p-4" data-testid="q-bytype-panel">
                <h3 className="font-semibold mb-3">Solves by Problem Type</h3>
                {(quantum?.by_problem_type || []).length === 0 ? (
                  <p className="text-sm text-[var(--qn-foreground-muted)]">No solves recorded yet.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Problem</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {quantum.by_problem_type.map((r, i) => (
                        <TableRow key={i} data-testid={`q-type-${r.problem_type}`}>
                          <TableCell className="text-xs font-mono">{r.problem_type}</TableCell>
                          <TableCell className="text-right">{r.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              <Card className="p-4 lg:col-span-2" data-testid="q-recent-panel">
                <h3 className="font-semibold mb-3">Recent Solves</h3>
                {(quantum?.recent || []).length === 0 ? (
                  <p className="text-sm text-[var(--qn-foreground-muted)]">No solves recorded yet.</p>
                ) : (
                  <div className="max-h-72 overflow-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>When</TableHead><TableHead>Problem</TableHead><TableHead>Solver</TableHead>
                        <TableHead className="text-right">Vars</TableHead><TableHead className="text-right">Solve ms</TableHead>
                        <TableHead className="text-right">QPU ms</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {quantum.recent.map((r, i) => (
                          <TableRow key={i} data-testid={`q-recent-${i}`}>
                            <TableCell className="text-[11px] text-[var(--qn-foreground-muted)]">{(r.ts || "").slice(11, 19)}</TableCell>
                            <TableCell className="text-xs font-mono">{r.problem_type}</TableCell>
                            <TableCell>
                              <Badge variant={r.is_qpu ? "default" : "secondary"} className="text-[10px]">{r.solver}</Badge>
                              {r.formulation && r.formulation !== "bqm_penalty" && (
                                <Badge className="ml-1 text-[9px] bg-emerald-600" data-testid={`q-recent-formulation-${i}`}>{r.formulation === "cqm_native" ? "CQM" : "DQM"}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-xs">{r.n_vars}</TableCell>
                            <TableCell className="text-right text-xs font-mono">{r.solve_time_ms}</TableCell>
                            <TableCell className="text-right text-xs font-mono">{r.qpu_access_time_ms || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            </div>

            {/* ── Leap usage / availability ── */}
            <Card className="p-4 mt-4" data-testid="q-leap-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Zap className="w-4 h-4" /> Leap Solver Inventory & Availability</h3>
                <div className="flex gap-2">
                  {!leapUsage?.availability?.available && (
                    <Button size="sm" variant="outline" onClick={logRestoration} data-testid="q-leap-restoration-btn">
                      <Mail className="w-4 h-4 mr-1" /> Request Leap Time Restoration
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={probeLeap} disabled={qBusy === "leap"} data-testid="q-leap-probe-btn">
                    {qBusy === "leap" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                    Check availability
                  </Button>
                </div>
              </div>
              {leapUsage?.inventory?.categories && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="q-leap-categories">
                  {Object.entries(leapUsage.inventory.categories).map(([cat, on]) => (
                    <Badge key={cat} variant={on ? "default" : "secondary"} className="text-[9px] uppercase">
                      {cat} {on ? "live" : "—"}
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Pulled live via dwave-cloud-client. Remaining solver-access seconds is dashboard-only (never fabricated); use the live probe for true availability.</p>
              {(leapUsage?.inventory?.solvers || []).length === 0 ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">{leapUsage?.inventory?.error || "No solver data."}</p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Solver</TableHead><TableHead>Category</TableHead>
                    <TableHead>Problem Types</TableHead><TableHead className="text-right">Min TL (s)</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {leapUsage.inventory.solvers.map((s, i) => (
                      <TableRow key={i} data-testid={`q-leap-solver-${i}`}>
                        <TableCell className="text-[11px] font-mono">{s.id}</TableCell>
                        <TableCell className="text-xs">{s.category}</TableCell>
                        <TableCell className="text-xs">{(s.supported_problem_types || []).join(", ")}</TableCell>
                        <TableCell className="text-right text-xs">{s.minimum_time_limit_s ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {leapUsage?.availability && (
                <div className="mt-3 p-3 rounded-lg bg-[var(--qn-background-subtle)] text-xs" data-testid="q-leap-availability">
                  <Badge variant={leapUsage.availability.available ? "default" : "secondary"} className="mb-1">
                    {leapUsage.availability.available ? "QPU AVAILABLE" : (leapUsage.availability.reason === "access_time_exhausted" ? "ACCESS TIME EXHAUSTED" : "UNAVAILABLE")}
                  </Badge>
                  <div className="text-[var(--qn-foreground-muted)] font-mono">{leapUsage.availability.message}</div>
                </div>
              )}
            </Card>

            {/* ── Benchmark harness ── */}
            <Card className="p-4 mt-4" data-testid="q-bench-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Gauge className="w-4 h-4" /> Head-to-Head Solver Benchmark</h3>
                <Button size="sm" variant="outline" onClick={runBenchmark} disabled={qBusy === "bench"} data-testid="q-bench-run-btn">
                  {qBusy === "bench" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Gauge className="w-4 h-4 mr-1" />}
                  Run benchmark
                </Button>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Exact (ExactSolver) / MIP (CBC) vs SA / Tabu / Greedy vs Leap Hybrid over the real QUBO matrices. Optimality gap is vs the exact/MIP optimum when available. Every energy is measured.</p>
              {!benchmark || benchmark.status === "none" ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">{benchmark?.message || "No benchmark run yet — click Run benchmark."}</p>
              ) : (
                <div className="space-y-4">
                  <div className="text-[11px] text-[var(--qn-foreground-subtle)]">Generated {benchmark.generated_at?.slice(0, 19)} · Ocean {benchmark.sdk?.["dwave-ocean-sdk"]} · dimod {benchmark.sdk?.dimod}</div>
                  {benchmark.results?.map((r, ri) => (
                    <div key={ri} data-testid={`q-bench-problem-${r.problem}`}>
                      <div className="text-sm font-semibold mb-1">{r.problem} <span className="text-xs font-normal text-[var(--qn-foreground-muted)]">· n={r.n_vars} vars · ref: {r.reference}</span></div>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Solver</TableHead><TableHead>Status</TableHead>
                          <TableHead className="text-right">Energy</TableHead><TableHead className="text-right">Gap %</TableHead>
                          <TableHead className="text-right">Wall ms</TableHead><TableHead className="text-right">QPU ms</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {r.solvers.map((s, si) => (
                            <TableRow key={si} data-testid={`q-bench-${r.problem}-${s.solver}`}>
                              <TableCell className="text-xs font-mono">{s.solver}{s.is_optimal && <Badge className="ml-1 text-[9px]">opt</Badge>}</TableCell>
                              <TableCell><Badge variant={s.status === "ok" ? "default" : "secondary"} className="text-[10px]">{s.status}</Badge></TableCell>
                              <TableCell className="text-right text-xs font-mono">{s.energy ?? "—"}</TableCell>
                              <TableCell className="text-right text-xs font-mono">{s.gap_pct ?? "—"}</TableCell>
                              <TableCell className="text-right text-xs">{s.wall_ms ?? "—"}</TableCell>
                              <TableCell className="text-right text-xs">{s.qpu_access_time_ms || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* ── CQM prototype ── */}
            <Card className="p-4 mt-4" data-testid="q-cqm-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Atom className="w-4 h-4" /> Native CQM Prototype (HAVEN)</h3>
                <Button size="sm" variant="outline" onClick={loadCqm} disabled={qBusy === "cqm"} data-testid="q-cqm-run-btn">
                  {qBusy === "cqm" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Atom className="w-4 h-4 mr-1" />}
                  Build & solve CQM
                </Button>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Expresses HAVEN's cardinality / per-state / exclusion rules as FIRST-CLASS CQM constraints (no penalty weights to tune) on LeapHybridCQMSampler, with a classical fallback so the demo always returns a feasible book.</p>
              {!cqm ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">Click Build &amp; solve CQM to generate the prototype.</p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs mb-2 flex flex-wrap gap-2">
                      <Badge variant={cqm.solver_used === "leap_hybrid_cqm" ? "default" : "secondary"}>{cqm.solver_used}</Badge>
                      <Badge variant="outline">{cqm.meta?.num_variables} vars · {cqm.meta?.num_constraints} constraints</Badge>
                      <Badge variant={cqm.verification?.cardinality_ok && cqm.verification?.per_state_cap_ok && cqm.verification?.excluded_states_ok ? "default" : "secondary"}>
                        {cqm.verification?.cardinality_ok && cqm.verification?.per_state_cap_ok && cqm.verification?.excluded_states_ok ? "all constraints satisfied" : "constraint violation"}
                      </Badge>
                    </div>
                    <h4 className="text-xs font-semibold mb-1 text-[var(--qn-foreground-muted)]">Native constraints vs penalty encoding</h4>
                    <Table>
                      <TableHeader><TableRow><TableHead>Constraint</TableHead><TableHead>Native</TableHead><TableHead>Replaces penalty</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(cqm.native_constraints || []).map((c, i) => (
                          <TableRow key={i} data-testid={`q-cqm-constraint-${i}`}>
                            <TableCell className="text-[11px]">{c.label}</TableCell>
                            <TableCell className="text-[11px] font-mono">{c.expr}</TableCell>
                            <TableCell className="text-[11px] text-[var(--qn-foreground-muted)]">{c.replaces_penalty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold mb-1 text-[var(--qn-foreground-muted)]">Selected portfolio (objective {cqm.objective_value})</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {(cqm.portfolio || []).map((p, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]" data-testid={`q-cqm-pick-${i}`}>{p.market_name}</Badge>
                      ))}
                    </div>
                    {cqm.leap_error && (
                      <p className="text-[11px] text-[var(--qn-foreground-muted)] mt-2 font-mono">Leap CQM: {cqm.leap_error.slice(0, 120)}</p>
                    )}
                  </div>
                </div>
              )}
            </Card>

            {/* ── HAVEN DQM (config layers → discrete; Phase 1) ── */}
            <Card className="p-4 mt-4" data-testid="q-dqm-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Atom className="w-4 h-4" /> HAVEN DQM — Config Layers as Discrete Variables</h3>
                <Button size="sm" variant="outline" onClick={runDqm} disabled={qBusy === "dqm"} data-testid="q-dqm-run-btn">
                  {qBusy === "dqm" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Atom className="w-4 h-4 mr-1" />}
                  Build & solve DQM
                </Button>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Encodes HAVEN's 6 per-market configuration layers (allocation / type / hold / financing / timing / renovation) as a <span className="font-semibold">DiscreteQuadraticModel</span> on <span className="font-mono">LeapHybridDQMSampler</span> — each one-hot binary group collapses to a single discrete variable. Additive: the production BQM path is untouched. Classical SA fallback while Leap DQM access is exhausted.</p>
              {!dqm || dqm.status === "none" ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]" data-testid="q-dqm-empty">{dqm?.message || "No DQM solve yet — click Build & solve DQM."}</p>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="q-dqm-reduction">
                    <div className="text-sm font-semibold text-emerald-400">{dqm.reduction_headline}</div>
                    <div className="text-[11px] text-[var(--qn-foreground-muted)] mt-1">Full model: {(dqm.meta?.equivalent_binary_vars || 0).toLocaleString()} equivalent binary → {(dqm.meta?.total_discrete_vars || 0).toLocaleString()} discrete ({dqm.meta?.reduction_pct}% overall reduction across {dqm.meta?.n_markets} markets × {dqm.meta?.config_layers} layers).</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="p-2 rounded bg-[var(--qn-background-subtle)]" data-testid="q-dqm-solver"><div className="text-[var(--qn-foreground-muted)]">Solver</div><Badge variant={dqm.is_qpu ? "default" : "secondary"} className="text-[10px] mt-0.5">{dqm.solver_used}</Badge></div>
                    <div className="p-2 rounded bg-[var(--qn-background-subtle)]"><div className="text-[var(--qn-foreground-muted)]">Discrete vars</div><div className="font-mono">{(dqm.meta?.total_discrete_vars || 0).toLocaleString()}</div></div>
                    <div className="p-2 rounded bg-[var(--qn-background-subtle)]"><div className="text-[var(--qn-foreground-muted)]">Solve ms</div><div className="font-mono">{dqm.solve_time_ms}</div></div>
                    <div className="p-2 rounded bg-[var(--qn-background-subtle)]"><div className="text-[var(--qn-foreground-muted)]">Energy</div><div className="font-mono">{dqm.energy}</div></div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-[11px] text-[var(--qn-foreground-muted)] mr-1">Selected {dqm.selected_count} markets:</span>
                    {(dqm.portfolio || []).slice(0, 24).map((p, i) => (
                      <Badge key={i} variant="outline" className="text-[10px]" data-testid={`q-dqm-pick-${i}`}>{p.market_name}</Badge>
                    ))}
                  </div>
                  {dqm.leap_error && <p className="text-[11px] text-amber-500 font-mono">Leap DQM: {String(dqm.leap_error).slice(0, 120)}</p>}
                </div>
              )}
            </Card>


            {/* ── HiGHS head-to-head benchmark corpus ── */}
            <Card className="p-4 mt-4" data-testid="q-benchmarks-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Gauge className="w-4 h-4" /> HiGHS Head-to-Head (D-Wave vs SA vs HiGHS)</h3>
                <Button size="sm" variant="outline" onClick={runBenchmarks} disabled={qBusy === "benchmarks"} data-testid="q-benchmarks-run-btn">
                  {qBusy === "benchmarks" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Gauge className="w-4 h-4 mr-1" />}
                  Run head-to-head
                </Button>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Apples-to-apples on the <span className="font-semibold">identical</span> QUBO: D-Wave Hybrid, Simulated Annealing, and HiGHS QUBO&rarr;MIP (McCormick — same variables &amp; objective) are scored on full-QUBO energy. <span className="font-mono">highs_selection_core</span> is a reduced market-selection MIP shown for context only (it omits the config/neighborhood vars &amp; pairwise terms — not scored on the full objective). HiGHS is the #1 open-source MIP solver (<span className="font-mono">pip install highspy</span>).</p>

              {ciBaseline && (
                <div className="mb-4 p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-background-subtle)]" data-testid="q-ci-baseline">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2"><Shield className="w-4 h-4" /> Advantage Ledger — CI guard</h4>
                    <Badge variant={ciBaseline.guard_status === "PASS" ? "default" : "destructive"} data-testid="q-ci-guard-status">{ciBaseline.guard_status}</Badge>
                  </div>
                  <p className="text-[11px] text-[var(--qn-foreground-muted)] mb-2">Permanent classical champion: <span className="font-mono">{ciBaseline.champion}</span>. A quantum solver is credited with advantage only if it ran natively on Leap <span className="font-semibold">and</span> measurably beat the champion — {ciBaseline.advantage_proven_count} proven of {ciBaseline.problems_evaluated} problems.</p>
                  <div className="space-y-1">
                    {(ciBaseline.verdicts || []).map((v, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px]" data-testid={`q-ci-verdict-${i}`}>
                        <span className="font-mono">{v.problem} <span className="text-[var(--qn-foreground-subtle)]">(base {v.baseline_solver})</span></span>
                        <Badge variant={v.verdict === "advantage_proven" ? "default" : "secondary"} className="text-[9px]">{v.verdict.replace(/_/g, " ")}</Badge>
                      </div>
                    ))}
                  </div>
                  {ciBaseline.violations && ciBaseline.violations.length > 0 && (
                    <p className="text-[11px] text-red-500 mt-2" data-testid="q-ci-violations">⚠ {ciBaseline.violations.length} unmeasured quantum-advantage claim(s) — CI would fail.</p>
                  )}
                </div>
              )}
              {!benchmarks || (benchmarks.total_benchmarks || 0) === 0 ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">No head-to-head runs yet — click Run head-to-head.</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg bg-[var(--qn-background-subtle)]"><div className="text-xl font-bold">{benchmarks.total_benchmarks}</div><div className="text-[11px] text-[var(--qn-foreground-muted)]">Total benchmarks</div></div>
                    {(benchmarks.wins_by_business_score || []).slice(0, 3).map((w, i) => (
                      <div key={i} className="p-3 rounded-lg bg-[var(--qn-background-subtle)]" data-testid={`q-bm-win-${w.solver}`}><div className="text-xl font-bold">{w.wins}</div><div className="text-[11px] text-[var(--qn-foreground-muted)] font-mono">{w.solver} wins</div></div>
                    ))}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold mb-1 text-[var(--qn-foreground-muted)]">Solver leaderboard (avg across all benchmarks)</h4>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Solver</TableHead><TableHead className="text-right">Runs</TableHead>
                        <TableHead className="text-right">Feasible %</TableHead><TableHead className="text-right">Avg score</TableHead>
                        <TableHead className="text-right">Avg time (ms)</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {(benchmarks.solver_summary || []).map((s, i) => (
                          <TableRow key={i} data-testid={`q-bm-solver-${s.solver}`}>
                            <TableCell className="text-xs font-mono">{s.solver}</TableCell>
                            <TableCell className="text-right text-xs">{s.runs}</TableCell>
                            <TableCell className="text-right text-xs">{s.feasible_pct}%</TableCell>
                            <TableCell className="text-right text-xs font-mono">{s.avg_business_score ?? "—"}</TableCell>
                            <TableCell className="text-right text-xs">{s.avg_solve_time_ms}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold mb-1 text-[var(--qn-foreground-muted)]">Recent runs (per problem)</h4>
                    <div className="max-h-80 overflow-auto space-y-3">
                      {(benchmarks.recent || []).map((r, ri) => (
                        <div key={ri} data-testid={`q-bm-recent-${ri}`}>
                          <div className="text-sm font-semibold mb-1">{r.problem_type} <span className="text-xs font-normal text-[var(--qn-foreground-muted)]">· n={r.n_vars}{r.qubo_pairwise_terms ? ` · ${r.qubo_pairwise_terms} pairwise terms` : ""}{r.tier ? ` · tier ${r.tier}` : ""} · winner: {(r.winner || {}).by_business_score || "—"}</span></div>
                          <Table>
                            <TableHeader><TableRow>
                              <TableHead>Solver</TableHead><TableHead>Feasible</TableHead>
                              <TableHead className="text-right">Vars / Constr</TableHead>
                              <TableHead className="text-right">Score</TableHead><TableHead className="text-right">Energy / EV</TableHead>
                              <TableHead className="text-right">Time ms</TableHead>
                            </TableRow></TableHeader>
                            <TableBody>
                              {Object.entries(r.results || {}).map(([name, v], si) => (
                                <TableRow key={si} data-testid={`q-bm-${r.problem_type}-${name}`}>
                                  <TableCell className="text-xs font-mono">{name}{(r.winner || {}).by_business_score === name && <Badge className="ml-1 text-[9px]">win</Badge>}</TableCell>
                                  <TableCell>{v.status ? <Badge variant="secondary" className="text-[10px]">{v.status}</Badge> : <Badge variant={v.feasible ? "default" : "secondary"} className="text-[10px]">{v.feasible ? "yes" : (v.mip_status || "no")}</Badge>}</TableCell>
                                  <TableCell className="text-right text-[11px] font-mono text-[var(--qn-foreground-muted)]">{v.n_vars ?? "—"}{v.n_constraints ? ` / ${v.n_constraints}` : ""}</TableCell>
                                  <TableCell className="text-right text-xs font-mono">{v.business_score ?? (v.market_reward_score != null ? `(${v.market_reward_score})` : "—")}</TableCell>
                                  <TableCell className="text-right text-xs font-mono">{v.energy ?? (v.total_expected_value ? `$${(v.total_expected_value / 1e6).toFixed(2)}M` : "—")}</TableCell>
                                  <TableCell className="text-right text-xs">{v.solve_time_ms ?? "—"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* ── Raw-QPU head-to-head (direct anneal vs Hybrid vs SA) ── */}
            <Card className="p-4 mt-4" data-testid="q-rawqpu-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Cpu className="w-4 h-4" /> Raw QPU vs Hybrid (direct anneal on dense sub-QUBOs)</h3>
                <Button size="sm" variant="outline" onClick={runRawQpu} disabled={qBusy === "rawqpu"} data-testid="q-rawqpu-run-btn">
                  {qBusy === "rawqpu" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Cpu className="w-4 h-4 mr-1" />}
                  Run raw-QPU anneal
                </Button>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">The one path Leap Hybrid hides: a <span className="font-semibold">direct QPU anneal</span> via <span className="font-mono">DWaveSampler + EmbeddingComposite</span> (minorminer auto-embedding) on small, dense sub-QUBOs carved from the real HAVEN &amp; proxy Q matrices — measured against Leap Hybrid, classical Simulated Annealing, and the exact optimum (n&nbsp;&le;&nbsp;18). Captures true annealing quality: energy, <span className="font-mono">qpu_access_time</span>, physical qubits, chain length and chain-break fraction.</p>

              {rawQpu?.note && (
                <div className="mb-3 p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-background-subtle)] text-[11px]" data-testid="q-rawqpu-note">
                  <span className={rawQpu.raw_qpu_ran ? "text-emerald-500 font-semibold" : "text-amber-500 font-semibold"}>{rawQpu.raw_qpu_ran ? "QPU anneal recorded" : "QPU unavailable"}</span> — {rawQpu.note}
                </div>
              )}

              {rawQpu?.embedding_analysis?.status === "ok" && (
                <div className="mb-3 p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-background-subtle)]" data-testid="q-rawqpu-embedding">
                  <h4 className="text-sm font-semibold mb-1 flex items-center gap-2"><Cpu className="w-3.5 h-3.5" /> Pre-submission embedding — real {rawQpu.embedding_analysis.target?.topology}</h4>
                  <p className="text-[11px] text-[var(--qn-foreground-muted)] mb-2">{rawQpu.embedding_analysis.source} · minor-embedded via minorminer onto <span className="font-mono">{rawQpu.embedding_analysis.target?.solver_id || rawQpu.embedding_analysis.target?.topology}</span> ({(rawQpu.embedding_analysis.target?.target_qubits || 0).toLocaleString()} qubits, {(rawQpu.embedding_analysis.target?.target_couplers || 0).toLocaleString()} couplers) — no QPU time consumed.</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                    {[["Logical vars", rawQpu.embedding_analysis.logical_vars_embedded],
                      ["Physical qubits", rawQpu.embedding_analysis.physical_qubits],
                      ["Overhead", `${rawQpu.embedding_analysis.qubit_overhead_ratio}×`],
                      ["Max chain", rawQpu.embedding_analysis.max_chain_length],
                      ["Avg chain", rawQpu.embedding_analysis.avg_chain_length],
                      ["Chain-break", rawQpu.embedding_analysis.chain_break_fraction ?? "n/a*"]].map(([lbl, val], i) => (
                      <div key={i} className="p-2 rounded bg-[var(--qn-background)]" data-testid={`q-rawqpu-emb-${i}`}>
                        <div className="text-sm font-mono font-semibold">{val}</div>
                        <div className="text-[9px] text-[var(--qn-foreground-muted)]">{lbl}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-[var(--qn-foreground-subtle)] mt-2">*chain-break fraction needs a live anneal (Leap access pending). Physical-qubit overhead quantifies the embedding cost of this dense structure on current hardware.</p>
                </div>
              )}

              {!rawQpu || !(rawQpu.results || []).length ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">No raw-QPU run yet — click Run raw-QPU anneal.</p>
              ) : (
                <div className="space-y-4">
                  {(rawQpu.results || []).map((r, ri) => (
                    <div key={ri} data-testid={`q-rawqpu-problem-${ri}`}>
                      <div className="text-sm font-semibold mb-1">{r.name} <span className="text-xs font-normal text-[var(--qn-foreground-muted)]">· {r.source} · n={r.n_vars} · {r.pairwise_terms} couplings · density {r.density} · ref: {r.reference}</span></div>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Solver</TableHead><TableHead>Status</TableHead>
                          <TableHead className="text-right">Energy</TableHead><TableHead className="text-right">Gap %</TableHead>
                          <TableHead className="text-right">Wall ms</TableHead><TableHead className="text-right">QPU ms</TableHead>
                          <TableHead className="text-right">Embedding (qubits · chain · breaks)</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {Object.entries(r.solvers || {}).map(([name, v], si) => (
                            <TableRow key={si} data-testid={`q-rawqpu-${ri}-${name}`}>
                              <TableCell className="text-xs font-mono">{name}{v.is_optimal && <Badge className="ml-1 text-[9px]">optimal</Badge>}</TableCell>
                              <TableCell><Badge variant={v.status === "ok" ? "default" : "secondary"} className="text-[10px]">{v.status}</Badge></TableCell>
                              <TableCell className="text-right text-xs font-mono">{v.energy ?? "—"}</TableCell>
                              <TableCell className="text-right text-xs font-mono">{v.gap_pct != null ? `${v.gap_pct}%` : "—"}</TableCell>
                              <TableCell className="text-right text-xs">{v.wall_ms ?? "—"}</TableCell>
                              <TableCell className="text-right text-xs font-mono">{v.qpu_access_time_ms ?? "—"}</TableCell>
                              <TableCell className="text-right text-[11px] font-mono text-[var(--qn-foreground-muted)]">{v.embedding?.physical_qubits != null ? `${v.embedding.physical_qubits}q · ${v.embedding.max_chain_length}/${v.embedding.avg_chain_length} · ${v.chain_break_fraction ?? "—"}` : (v.note ? v.note.slice(0, 40) : "—")}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* ── Batch Routing QUBO (spec §4 scenario table) ── */}
            <Card className="p-4 mt-4" data-testid="q-batch-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Network className="w-4 h-4" /> Batch Routing QUBO (argmin vs SA vs D-Wave vs CQM vs HiGHS)</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={logRestoration} data-testid="q-batch-leap-email-btn">
                    <Mail className="w-4 h-4 mr-1" /> Request Leap Time Restoration
                  </Button>
                  <Button size="sm" variant="outline" onClick={runBatchRouting} disabled={qBusy === "batch"} data-testid="q-batch-run-btn">
                    {qBusy === "batch" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Network className="w-4 h-4 mr-1" />}
                    Run scenario table
                  </Button>
                </div>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Global assignment of a <span className="font-semibold">batch</span> of N concurrent requests across M models under shared rate limits, quality floors, latency SLAs and a batch budget (Global InTech spec v3). NP-hard (reduction from GAP). Small/medium are fully solved; enterprise/large/edge are <span className="font-semibold">MEASURED</span> — the HiGHS native MIP runs with a real 10s wall clock to find where classical branch-and-cut stops closing the gap; Vista scale is analytical. The production per-request argmin routing is untouched. Live opt-in endpoint: <span className="font-mono">POST /api/v1/qubo/batch-route</span>.</p>

              {!batchRouting || !(batchRouting.results || []).length ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">No scenario run yet — click Run scenario table.</p>
              ) : (
                <div className="space-y-2">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Scenario</TableHead><TableHead className="text-right">Vars</TableHead>
                      <TableHead className="text-right">Rate coupling</TableHead><TableHead>Dispatched</TableHead>
                      <TableHead>Expected winner</TableHead><TableHead>Measured winner</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(batchRouting.results || []).map((r, i) => (
                        <TableRow key={i} data-testid={`q-batch-scenario-${r.name}`}>
                          <TableCell className="text-xs">{r.label} {r.mode === "analytical" && <Badge variant="secondary" className="ml-1 text-[9px]">ANALYTICAL</Badge>}{r.mode === "measured" && <Badge className="ml-1 text-[9px] bg-emerald-600">MEASURED</Badge>}{r.mode === "solved" && <Badge variant="outline" className="ml-1 text-[9px]">SOLVED</Badge>}</TableCell>
                          <TableCell className="text-right text-xs font-mono">{(r.n_vars || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-right text-xs font-mono">{(r.rate_coupling_terms || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-xs font-mono">{r.dispatched_solver}</TableCell>
                          <TableCell className="text-xs">{r.expected_winner}</TableCell>
                          <TableCell className="text-xs font-mono">{r.measured_winner || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {(batchRouting.results || []).filter(r => r.mode === "solved" || r.mode === "measured").map((r, ri) => (
                    <div key={ri} className="mt-3" data-testid={`q-batch-detail-${r.name}`}>
                      <div className="text-sm font-semibold mb-1">{r.label} <span className="text-xs font-normal text-[var(--qn-foreground-muted)]">· budget ${r.budget_usd}{r.density != null && ` · density ${r.density}`}{r.mode === "measured" && " · HiGHS native MIP @ 10s wall clock"}</span></div>
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Solver</TableHead><TableHead className="text-right">Cost $</TableHead>
                          <TableHead className="text-right">Rate viol.</TableHead><TableHead className="text-right">Quality feasible</TableHead>
                          <TableHead className="text-right">Cost reduction</TableHead><TableHead className="text-right">Time ms</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {Object.entries(r.solvers || {}).map(([name, v], si) => (
                            <TableRow key={si} data-testid={`q-batch-${r.name}-${name}`}>
                              <TableCell className="text-xs font-mono">
                                {name}{r.measured_winner === name && <Badge className="ml-1 text-[9px]">win</Badge>}
                                {v.timed_out && <Badge variant="destructive" className="ml-1 text-[9px]">TIMED OUT · {v.mip_gap_at_termination != null ? `gap ${Math.round(v.mip_gap_at_termination * 100)}%` : "no proven bound"}</Badge>}
                                {name === "highs_native_mip" && v.status === "ok" && !v.timed_out && <Badge variant="outline" className="ml-1 text-[9px]">optimal</Badge>}
                                {v.fell_back && <div className="text-[9px] text-amber-500">{v.label}</div>}
                                {v.status === "leap_pending" && <div className="text-[9px] text-amber-500">{v.label}</div>}
                                {v.status === "not_viable" && <div className="text-[9px] text-red-400">penalty QUBO unbuildable at scale</div>}
                              </TableCell>
                              <TableCell className="text-right text-xs font-mono">{v.total_cost_usd != null ? `$${v.total_cost_usd.toFixed(6)}` : (v.status !== "ok" ? <Badge variant="secondary" className="text-[9px]">{v.status}</Badge> : "—")}{name === "argmin" && v.effective_cost_with_retries_usd != null && <div className="text-[9px] text-[var(--qn-foreground-muted)]">eff ${v.effective_cost_with_retries_usd.toFixed(6)} ({v.retried_requests} retries)</div>}</TableCell>
                              <TableCell className="text-right text-xs">{v.rate_limit_violations != null ? <Badge variant={v.rate_limit_violations === 0 ? "default" : "destructive"} className="text-[9px]">{v.rate_limit_violations}</Badge> : "—"}</TableCell>
                              <TableCell className="text-right text-xs">{v.quality_feasible_pct != null ? `${v.quality_feasible_pct}%` : "—"}</TableCell>
                              <TableCell className="text-right text-xs font-mono">{v.cost_reduction_pct != null ? `${v.cost_reduction_pct}%` : "—"}</TableCell>
                              <TableCell className="text-right text-xs">{v.solve_time_ms ?? "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {r.mode === "measured" && r.measured_winner == null && (
                        <p className="text-[11px] text-amber-500 mt-1" data-testid={`q-batch-verdict-${r.name}`}>Empirical threshold: HiGHS native MIP could NOT prove optimality within 10s at {(r.n_vars || 0).toLocaleString()} variables — this is the classically intractable regime where D-Wave's native CQM hybrid solver is required.</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Phase 4 — Why Quantum is Required at Vista Scale */}
              <div className="mt-4 p-3 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-background-subtle)]" data-testid="q-vista-necessity">
                <h4 className="text-sm font-semibold mb-1 flex items-center gap-2"><Network className="w-4 h-4" /> Why Quantum is Required at Vista Scale</h4>
                <p className="text-[11px] text-[var(--qn-foreground-muted)] leading-relaxed">At N=10,000 concurrent requests across M=200 models, the rate-limit coupling term generates approximately 10 billion quadratic interactions. The LP relaxation of this problem is provably weak due to the multi-dimensional knapsack structure of the overlapping per-provider and per-tier budget constraints. Classical branch-and-cut solvers cannot certify optimality within real-time latency constraints at this scale. D-Wave Hybrid CQM is the only production-viable solver for this problem size.</p>
              </div>
            </Card>

            {/* ── Scale-Out necessity studies (HAVEN Tier-4 + CSH Large) ── */}
            <Card className="p-4 mt-4" data-testid="q-scaleout-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Gauge className="w-4 h-4" /> Scale-Out Necessity Studies (HAVEN Tier-4 · CSH Large)</h3>
                <Button size="sm" variant="outline" onClick={runScaleOut} disabled={qBusy === "scaleout"} data-testid="q-scaleout-run-btn">
                  {qBusy === "scaleout" ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Gauge className="w-4 h-4 mr-1" />}
                  Run scale-out
                </Button>
              </div>
              <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">Two more problem classes scaled to be quantum-class in <span className="font-semibold">size</span> (~10–12k binaries), each measured on the HiGHS native MIP with a 10s wall clock. Honest by construction: whether HiGHS proves optimality is measured, not assumed.</p>
              {!scaleOut || !(scaleOut.studies || []).length ? (
                <p className="text-sm text-[var(--qn-foreground-muted)]">No scale-out study yet — click Run scale-out.</p>
              ) : (
                <>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Study</TableHead><TableHead>Class</TableHead>
                      <TableHead className="text-right">Vars</TableHead><TableHead className="text-right">Constraints</TableHead>
                      <TableHead>HiGHS native MIP @10s</TableHead><TableHead className="text-right">Time ms</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(scaleOut.studies || []).map((s, i) => (
                        <TableRow key={i} data-testid={`q-scaleout-${s.name}`}>
                          <TableCell className="text-xs">{s.label}</TableCell>
                          <TableCell className="text-[11px] text-[var(--qn-foreground-muted)]">{s.problem_class}</TableCell>
                          <TableCell className="text-right text-xs font-mono">{(s.n_vars || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-right text-xs font-mono">{(s.n_constraints || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-xs">
                            {s.timed_out
                              ? <Badge variant="destructive" className="text-[9px]">TIMED OUT · gap {s.mip_gap_at_termination != null ? `${Math.round(s.mip_gap_at_termination * 100)}%` : "?"}</Badge>
                              : <Badge className="text-[9px]">optimal (gap 0)</Badge>}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">{s.solve_time_ms}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <p className="text-[11px] text-amber-500 mt-2" data-testid="q-scaleout-note">{scaleOut.dwave_note}</p>
                </>
              )}
            </Card>


          </TabsContent>
        </Tabs>
      </main>

      {/* Leap Time Restoration Email Dialog */}
      <Dialog open={leapEmailOpen} onOpenChange={setLeapEmailOpen}>
        <DialogContent className="max-w-2xl" data-testid="q-leap-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Request Leap Time Restoration</DialogTitle>
            <DialogDescription>Pre-formatted email to D-Wave to restore solver-access time so the batch-routing workloads run on the live Leap Hybrid BQM/CQM solvers.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div><span className="text-[var(--qn-foreground-muted)]">To:</span> <span className="font-mono" data-testid="q-leap-email-to">{LEAP_EMAIL.to}</span></div>
            <div><span className="text-[var(--qn-foreground-muted)]">Subject:</span> <span className="font-mono text-xs">{LEAP_EMAIL.subject}</span></div>
            <pre className="p-3 rounded-lg bg-[var(--qn-background-subtle)] text-[11px] whitespace-pre-wrap max-h-72 overflow-auto" data-testid="q-leap-email-body">{LEAP_EMAIL.body}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`To: ${LEAP_EMAIL.to}\nSubject: ${LEAP_EMAIL.subject}\n\n${LEAP_EMAIL.body}`); toast.success("Email copied to clipboard"); }} data-testid="q-leap-email-copy-btn">
              <Copy className="w-4 h-4 mr-1" /> Copy email
            </Button>
            <Button onClick={() => { window.location.href = `mailto:${LEAP_EMAIL.to}?subject=${encodeURIComponent(LEAP_EMAIL.subject)}&body=${encodeURIComponent(LEAP_EMAIL.body)}`; }} data-testid="q-leap-email-open-btn">
              <Mail className="w-4 h-4 mr-1" /> Open in mail client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Tenant Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Create Tenant</DialogTitle>
            <DialogDescription>Provisions a new organization with an owner account. Leave the password blank to auto-generate one.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Organization Name</Label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Acme Corp" data-testid="create-tenant-name" />
            </div>
            <div>
              <Label>Admin Email</Label>
              <Input type="email" value={createForm.admin_email} onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })} placeholder="admin@acme.com" data-testid="create-tenant-email" />
            </div>
            <div>
              <Label>Admin Password <span className="text-[var(--qn-foreground-subtle)]">(optional)</span></Label>
              <Input type="text" value={createForm.admin_password} onChange={(e) => setCreateForm({ ...createForm, admin_password: e.target.value })} placeholder="Auto-generated if blank" data-testid="create-tenant-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createTenant} disabled={busy} data-testid="create-tenant-submit">
              {busy ? "Creating…" : "Create Tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Tenant Dialog */}
      <Dialog open={!!manageTenant} onOpenChange={(o) => !o && setManageTenant(null)}>
        <DialogContent data-testid="manage-tenant-dialog">
          <DialogHeader>
            <DialogTitle>Manage {manageTenant?.name}</DialogTitle>
            <DialogDescription>Toggle feature entitlements and issue scoped API keys for this tenant.</DialogDescription>
          </DialogHeader>

          <div className="space-y-1">
            <h4 className="text-sm font-semibold mb-2">Feature Entitlements</h4>
            {featureCatalog.map((f) => (
              <div key={f.id} className="flex items-center justify-between py-2 border-b border-[var(--qn-border)] last:border-0">
                <div>
                  <div className="text-sm font-medium">{f.label}</div>
                  <div className="text-xs text-[var(--qn-foreground-muted)]">{f.desc}</div>
                </div>
                <Switch
                  checked={!!manageFeatures[f.id]}
                  onCheckedChange={(v) => setManageFeatures({ ...manageFeatures, [f.id]: v })}
                  data-testid={`entitlement-switch-${f.id}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-[var(--qn-border)]">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-1"><KeyRound className="w-4 h-4" /> API Access</h4>
                <p className="text-xs text-[var(--qn-foreground-muted)]">Issues a key with <code>Swarm/execute</code> + <code>LLM-Gateway/chat</code> scopes.</p>
              </div>
              <Button size="sm" variant="outline" onClick={generateKey} disabled={busy} data-testid="generate-tenant-key-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Generate Key
              </Button>
            </div>
            {generatedKey && (
              <div className="mt-3 p-3 rounded-lg bg-[var(--qn-surface-hover)] border border-[var(--qn-border)]" data-testid="generated-key-box">
                <p className="text-xs text-amber-400 mb-1">Copy this key now — it won't be shown again.</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono break-all flex-1">{generatedKey}</code>
                  <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(generatedKey); toast.success("Copied"); }} data-testid="copy-generated-key-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            <div className="mt-3" data-testid="tenant-keys-list">
              {tenantKeys.length === 0 ? (
                <p className="text-xs text-[var(--qn-foreground-muted)]">No API keys issued for this tenant yet.</p>
              ) : tenantKeys.map((k) => (
                <div key={k.key_preview} className="flex items-center justify-between py-2 border-b border-[var(--qn-border)] last:border-0" data-testid={`tenant-key-${k.key_preview}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{k.name}</div>
                    <div className="text-xs font-mono text-[var(--qn-foreground-muted)]">{k.key_preview}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {k.is_active
                      ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge>
                      : <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-500/30">Revoked</Badge>}
                    {k.is_active && (
                      <Button size="sm" variant="destructive" onClick={() => revokeTenantKey(k.key_preview)} data-testid={`revoke-tenant-key-${k.key_preview}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManageTenant(null)}>Close</Button>
            <Button onClick={saveEntitlements} disabled={busy} data-testid="save-entitlements-btn">
              {busy ? "Saving…" : "Save Entitlements"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
